/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { CSSProperties, memo, ReactNode } from "react"
import { SortableElement, SortableHandle } from "react-sortable-hoc"
import { areEqual } from "react-window"
import { Row, Col } from "@duxton-web/ui"
import { VerticalDragUniversal } from "@duxton-web/icons"

import { useSelector } from "react-redux"
import { AppState } from "store/store.type"

export const dividerSyle = css`
  border-bottom: 1px solid #ccd6dd;
`

export const dragHandleStyle = css`
  .drag-handle-container {
    margin-right: 8px;
  }
`

const hoverDraggableStyle = css`
  &:hover {
    cursor: row-resize;
  }
`

export const hoverSelectableStyle = (isSellingTimeStructure: boolean) =>
  css`
    .countWrapper {
      margin-left: 8px;
    }
    .menuBtn {
      margin-left: 8px;
    }
    .checkbox-container {
      margin-left: 8px;
    }
    .editBtn {
      margin-left: 8px;
    }
    :not(:hover) {
      .show-hover-container :not(.show) {
        display: none;
      }
    }
    &:hover {
      .menuBtn {
        background-color: #f3f6fb;
      }
      background-color: #f3f6fb;
      cursor: pointer;
    }
    .editBtn :not(.show) {
      display: none;
    }
    ${isSellingTimeStructure
      ? css`
          :not(:hover) {
            .checkbox-container :not(.show) {
              display: none;
            }
          }
          &:hover {
            .countWrapper :not(.show) {
              display: none;
            }
          }
        `
      : css`
          .checkbox-container :not(.show) {
            display: none;
          }
        `}
  `

interface ItemRowProps {
  item: any
  style: CSSProperties
  useDragHandle?: boolean
  renderRowContent(item: any): ReactNode
  isItemSelected?(item: any): boolean
  onClick?(event: React.MouseEvent<HTMLElement>, item: any): void
  draggable: boolean
}

const DragHandle = SortableHandle(
  ({ useDragHandle }: { useDragHandle?: boolean }) => (
    <div>
      <VerticalDragUniversal
        css={css`
          ${useDragHandle && hoverDraggableStyle}
        `}
      />
    </div>
  ),
)

const ItemRow = memo(
  ({
    item,
    style,
    renderRowContent,
    useDragHandle,
    onClick,
    draggable,
  }: ItemRowProps) => {
    const { isSellingTimeStructure } = useSelector(
      (state: AppState) => state?.menu,
    )

    return (
      <Row
        align="middle"
        wrap={false}
        style={style}
        css={css`
          padding: 0 24px;
          background-color: #ffffff;
          ${dragHandleStyle}
          ${dividerSyle}
          ${!useDragHandle && hoverDraggableStyle}
          ${hoverSelectableStyle(isSellingTimeStructure)}
        `}
        className="sortable-item-row"
        onClick={e => onClick?.(e, item)}
      >
        {draggable && (
          <Col className="drag-handle-container">
            <DragHandle useDragHandle={useDragHandle} />
          </Col>
        )}
        <Col flex="auto">{renderRowContent(item)}</Col>
      </Row>
    )
  },
  areEqual,
)

export const SortableItemRow = SortableElement((props: ItemRowProps) => (
  <ItemRow {...props} />
))
